import {
  useState,
  useEffect,
  useRef,
  memo,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { ROLES } from "../constant/roles";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import SelectProjectTemplate from "../components/SelectProjectTemplate";
import {
  useDeleteProject,
  useDuplicateProject,
  useProjectTemplatesQuery,
  useDuplicateProjectTemplate,
  useDeleteProjectTemplateMutation,
  GetTemplatesParams,
  getProjectStats,
  useCloseProject,
} from "../api/projectManagementApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import {
  getStatusBadge,
  getDifficultyBadge,
  StatCard,
} from "../utils/projectManagement.utils";
import { Progress } from "../components/ui/progress";
import { TabNavigation } from "../components/ui/tab-navigation";
import { PageHeader } from "../components/ui/page-header";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Trash2,
  Edit,
  Eye,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Bookmark,
  Ban,
  Calendar,
  User,
  FileText,
  Link,
  Layers,
  FileStack,
  FileSymlink,
  X,
  Upload,
  Pencil,
  Copy,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigation } from "../App";
import { useDebounce } from "../hooks/useDebounce";
import { format, parseISO } from "date-fns";
//import useNavigation from "../components/Context/NavigationContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
// import BitsDataScienceAPI from "../src/BitsDataScienceAPI"; // not needed after refactor
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { MaterialTable } from "../components/ui/MaterialTable";
import type { MRT_ColumnDef } from "material-react-table";
import {
  useEnhancedProjectsQuery,
  GetEnhancedProjectsParams,
  EnhancedProject,
  EnhancedProjectsResponseData,
} from "../api/projectManagementApi";
import type {
  RefetchOptions,
  QueryObserverResult,
} from "@tanstack/react-query";
import {
  useLTIContextsQuery,
  useLTIContextMembersQuery,
  useUnassignedResourceLinksQuery,
  useLinkProjectMutation,
  LTIContext,
  LTIContextMember,
  LTIResourceLink,
} from "../api/ltiApi";
import { useNavigate, useLocation } from "react-router-dom";
import paths from "../routes";
import { useQuery } from "@tanstack/react-query";
import { useProjectLink } from "../contexts/ProjectLinkContext";
import {
  useCreateTemplateWithProjectIdMutation,
  useConvertProjectToTemplateMutation,
  useStaffMemberQuery,
} from "../api/createProjectApi";
import {
  usePracticeProjectsQuery,
  usePracticeProjectStatsQuery,
  useDeletePracticeProjectMutation,
  useDuplicatePracticeProjectMutation,
  usePublishPracticeProjectMutation,
  useUnpublishPracticeProjectMutation,
} from "../api/practiceProjectsApi";
import type { PracticeProject } from "../types/practiceProjects";

interface TemplateData {
  id: string;
  name: string;
  description: string;
  projectData: any;
  createdAt: string;
  createdBy: string;
  category: string;
  tags: string[];
  usageCount: number;
}

interface LinkProjectModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  projectId: string;
  platformId?: string;
  createdBy?: string;
  refetchProjects: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<EnhancedProjectsResponseData, Error>>;
}

const LinkProjectModal = ({
  isOpen,
  setIsOpen,
  projectId,
  refetchProjects,
  platformId,
  createdBy,
}: LinkProjectModalProps) => {
  const { courseId, resourceLinkId, clearProjectLinkParams } = useProjectLink();
  const linkProjectMutation = useLinkProjectMutation();

  // State management
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>(
    [],
  );
  const [selectedTaIds, setSelectedTaIds] = useState<string[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<string>("");

  // Auto-populate from URL parameters
  useEffect(() => {
    if (isOpen && courseId) {
      setSelectedCourse(courseId);
    }
  }, [isOpen, courseId]);

  useEffect(() => {
    if (isOpen && resourceLinkId && selectedCourse) {
      setSelectedAssignment(resourceLinkId);
    }
  }, [isOpen, resourceLinkId, selectedCourse]);

  // Fetch courses for linking
  const { data: contextsData, isLoading: contextsLoading } =
    useLTIContextsQuery({ enabled: isOpen, forLinking: true });
  const contexts = Array.isArray(contextsData) ? contextsData : [];

  // Get platform ID from selected course
  const selectedContext = contexts.find((ctx) => ctx.id === selectedCourse);
  const selectedPlatformId = selectedContext?.platformId || platformId;

  // Fetch instructors and TAs for selected course
  const { data: instructorsData = [], isLoading: instructorsLoading } =
    useLTIContextMembersQuery(
      {
        contextId: selectedCourse,
        platformId: selectedPlatformId,
        role: `${ROLES.INSTRUCTOR},${ROLES.TA}`,
      },
      isOpen && !!selectedCourse && !!selectedPlatformId,
    );
  const instructors = Array.isArray(instructorsData) ? instructorsData : [];
  useEffect(() => {
    // 1. Ensure instructorsData is ready and createdBy exists
    if (!createdBy || !Array.isArray(instructorsData)) {
      return;
    }

    // 2. Filter instructors (only those with roleName === "instructor")
    const instructorList = instructorsData.filter(
      (item) => item.roleName === "instructor",
    );

    // 3. Check if createdBy ID exists in the filtered list
    const isCreatedByValidInstructor = instructorList.some(
      (instructor) => instructor.id === createdBy,
    );

    // 4. If valid and not already selected, add it
    if (
      isCreatedByValidInstructor &&
      !selectedInstructorIds.includes(createdBy)
    ) {
      setSelectedInstructorIds((prev) => [...prev, createdBy]);
    }
  }, [instructorsData, createdBy]);

  // Fetch assignments for selected course
  const { data: assignmentsData = [], isLoading: assignmentsLoading } =
    useUnassignedResourceLinksQuery(
      {
        contextId: selectedCourse,
        platformId: selectedPlatformId,
      },
      isOpen && !!selectedCourse && !!selectedPlatformId,
    );
  const assignments = Array.isArray(assignmentsData) ? assignmentsData : [];

  // Separate instructors and TAs
  const instructorList = instructors.filter((m) => m.roleName === "instructor");
  const taList = instructors.filter((m) => m.roleName === "ta");

  // Event handlers
  const handleClose = () => {
    setIsOpen(false);
    setSelectedCourse("");
    setSelectedInstructorIds([]);
    setSelectedTaIds([]);
    setSelectedAssignment("");
    clearProjectLinkParams();
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourse(courseId);
    setSelectedInstructorIds([]);
    setSelectedTaIds([]);
    setSelectedAssignment("");
  };

  const toggleInstructor = (instructorId: string) => {
    setSelectedInstructorIds((prev) =>
      prev.includes(instructorId)
        ? prev.filter((id) => id !== instructorId)
        : [...prev, instructorId],
    );
  };

  const toggleTA = (taId: string) => {
    setSelectedTaIds((prev) =>
      prev.includes(taId) ? prev.filter((id) => id !== taId) : [...prev, taId],
    );
  };

  const handleLinkProject = async () => {
    if (
      !selectedCourse ||
      !selectedAssignment ||
      (selectedInstructorIds.length === 0 && selectedTaIds.length === 0)
    ) {
      toast.error(
        "Please select a course, assignment, and at least one instructor or TA",
      );
      return;
    }

    try {
      await linkProjectMutation.mutateAsync({
        resourceLinkId: selectedAssignment,
        projectId,
        instructor_ids: selectedInstructorIds,
        teaching_ass_id: selectedTaIds,
      });
      refetchProjects();
      clearProjectLinkParams();
      toast.success("Project linked successfully!");
      handleClose();
    } catch (error) {
      toast.error("Failed to link project. Please try again.");
    }
  };

  const isLinkDisabled =
    !selectedCourse ||
    !selectedAssignment ||
    (selectedInstructorIds.length === 0 && selectedTaIds.length === 0) ||
    linkProjectMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-sm bg-white border transparent-overlay shadow-lg max-h-[95vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg font-semibold">
            Link Project to LMS Assignment
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-2 overflow-x-visible">
          {/* Step 1: Course Selection */}
          <div>
            <Label htmlFor="course" className="text-sm font-medium">
              Course <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedCourse}
              onValueChange={handleCourseChange}
              disabled={contextsLoading}
            >
              <SelectTrigger id="course" className="mt-2 w-full">
                <SelectValue
                  placeholder={
                    contextsLoading ? "Loading courses..." : "Select Course"
                  }
                />
              </SelectTrigger>
              <SelectContent className="z-[150]">
                {contextsLoading ? (
                  <SelectItem value="loading" disabled>
                    Loading...
                  </SelectItem>
                ) : contexts.length === 0 ? (
                  <SelectItem value="no-courses" disabled>
                    No courses available
                  </SelectItem>
                ) : (
                  contexts.map((context) => (
                    <SelectItem key={context.id} value={context.id}>
                      {context.contextTitle} ({context.contextLabel})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Step 2: Instructors Selection */}
          {selectedCourse && (
            <div>
              <Label className="text-sm font-medium">
                Instructors <span className="text-red-500">*</span>
              </Label>
              <Card className="mt-2">
                <CardContent className="p-3">
                  {instructorsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                      <span className="ml-2 text-sm text-muted-foreground">
                        Loading...
                      </span>
                    </div>
                  ) : instructorList.length === 0 ? (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No instructors found
                    </div>
                  ) : (
                    <>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {instructorList.map((instructor) => (
                          <div
                            key={instructor.id}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                            onClick={() => toggleInstructor(instructor.id)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedInstructorIds.includes(
                                instructor.id,
                              )}
                              onChange={() => {}}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {instructor.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {instructor.email}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {selectedInstructorIds.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-blue-600 font-medium">
                            {selectedInstructorIds.length} instructor
                            {selectedInstructorIds.length > 1 ? "s" : ""}{" "}
                            selected
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Teaching Assistants Selection */}
          {/* {selectedCourse && (
            <div>
              <Label className="text-sm font-medium">Teaching Assistants</Label>
              <Card className="mt-2">
                <CardContent className="p-3">
                  {instructorsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                      <span className="ml-2 text-sm text-muted-foreground">
                        Loading...
                      </span>
                    </div>
                  ) : taList.length === 0 ? (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No teaching assistants found
                    </div>
                  ) : (
                    <>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {taList.map((ta) => (
                          <div
                            key={ta.id}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                            onClick={() => toggleTA(ta.id)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedTaIds.includes(ta.id)}
                              onChange={() => {}}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {ta.name}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {ta.email}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {selectedTaIds.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-blue-600 font-medium">
                            {selectedTaIds.length} TA
                            {selectedTaIds.length > 1 ? "s" : ""} selected
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )} */}

          {/* Step 4: Assignment Selection */}
          {selectedCourse && (
            <div className="relative z-10">
              <Label htmlFor="assignment" className="text-sm font-medium">
                Assignment <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedAssignment}
                onValueChange={setSelectedAssignment}
                disabled={assignmentsLoading}
              >
                <SelectTrigger id="assignment" className="mt-2 w-full bg-white">
                  <SelectValue
                    placeholder={
                      assignmentsLoading
                        ? "Loading assignments..."
                        : "Select Assignment"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white">
                  {assignmentsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : assignments.length === 0 ? (
                    <SelectItem value="no-assignments" disabled>
                      No assignments available
                    </SelectItem>
                  ) : (
                    assignments.map((assignment) => (
                      <SelectItem key={assignment.id} value={assignment.id}>
                        {assignment.resourceLinkTitle}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex gap-2 mt-6 flex-shrink-0">
          <Button
            onClick={handleClose}
            variant="outline"
            className="flex-1"
            disabled={linkProjectMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLinkProject}
            className="flex-1 bg-bits-blue hover:bg-bits-blue-700"
            disabled={isLinkDisabled}
          >
            {linkProjectMutation.isPending ? "Linking..." : "Link Project"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeleteProjectModal = ({
  isOpen,
  onClose,
  refetchProjects,
  projectName = "Untitled Project",
  projectId,
}: {
  isOpen: boolean;
  onClose: () => void;
  refetchProjects: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<EnhancedProjectsResponseData, Error>>;
  projectName?: string;
  projectId: string;
}) => {
  const { mutate: deleteProjectMutation, isPending } = useDeleteProject();
  const handleDelete = () => {
    deleteProjectMutation(projectId, {
      onSuccess: () => {
        toast.success("Project deleted successfully");
        refetchProjects();
        onClose(); // close modal after success
      },
      onError: () => {
        onClose();
        toast.error("Failed to delete project");
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-white border transparent-overlay">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-black text-[18px]">
            Delete Project?
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <p className="text-gray-700 mb-4 text-[14px]">
            Are you sure you want to delete{" "}
            <span className="font-semibold break-all">{projectName}</span>?
          </p>
        </div>

        <div className="flex flex-row-reverse gap-4 mt-6">
          <button
            onClick={handleDelete}
            disabled={isPending}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CloseProjectModal = ({
  isOpen,
  onClose,
  refetchProjects,
  projectName = "Untitled Project",
  projectId,
}: {
  isOpen: boolean;
  onClose: () => void;
  refetchProjects: () => void;
  projectName?: string;
  projectId: string;
}) => {
  const { mutate: closeProjectMutation, isPending: isClosePending } =
    useCloseProject();

  const handleClose = () => {
    closeProjectMutation(projectId, {
      onSuccess: (response: any) => {
        // ✅ Access server response safely
        const message = response?.data?.data?.error?.message;

        if (message) {
          toast.info(message);
        } else {
          toast.success("Project closed successfully");
        }

        refetchProjects();
        onClose();
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.data?.message ||
          error?.response?.data?.message ||
          "Failed to close project";

        toast.error(message);
        onClose();
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-white border transparent-overlay">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-black text-[18px]">
            Close Project?
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <p className="text-gray-700 mb-4 text-[14px]">
            Are you sure you want to close{" "}
            <span className="font-semibold">{projectName}</span>?
          </p>
        </div>

        <div className="flex flex-row-reverse gap-4 mt-6">
          <button
            onClick={handleClose}
            disabled={isClosePending}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
          >
            {isClosePending ? "Closing..." : "Close"}
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function ProjectManagementPage() {
  const { navigateTo } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setProjectLinkParams } = useProjectLink();
  const [activeTab, setActiveTab] = useState<
    "all-projects" | "templates" | "practice-projects" | "analytics"
  >("all-projects");
  const [templateDialogProjectId, setTemplateDialogProjectId] = useState<
    string | number | undefined
  >(undefined);
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProjectIdToLink, setSelectedProjectId] = useState("");
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<
    string | undefined
  >(undefined);

  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenSelectTemplate, setIsSelectTemplate] = useState(false);
  const [trackSelectTemplate, setTrackSelectTemplate] = useState("newProject");
  const [isProjectPreview, setIsProjectPreview] = useState(false);
  const [projectId, setProjectId] = useState<string>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);
  const [deletePracticeProjectId, setDeletePracticeProjectId] = useState<
    string | null
  >(null);
  const [unpublishPracticeProjectId, setUnpublishPracticeProjectId] = useState<
    string | null
  >(null);
  const [statsCarouselIndex, setStatsCarouselIndex] = useState(0);
  const { mutate: duplicateProjectMutation, isPending } = useDuplicateProject();
  const [closeProjectId, setCloseProjectId] = useState<string | null>(null);
  const { mutateAsync } = useConvertProjectToTemplateMutation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [courseFilter, setCourseFilter] = useState<string | undefined>(
    undefined,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const {
    data: stats,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["projectStats"],
    queryFn: getProjectStats,
  });

  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [difficultyFilter, setDifficultyFilter] = useState<string | undefined>(
    undefined,
  );
  const [isLinkedFilter, setIsLinkedFilter] = useState<string | undefined>(
    undefined,
  );
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const handleModalClose = () => {
    setIsModalOpen2(false);
    setCreationType("newProject");
  };
  const [createdBy, setCreatedBy] = useState<string | undefined>(undefined);
  const handleContinue = () => {
    setIsModalOpen2(false);

    if (creationType === "useTemplate") {
      setShowTemplateSelector(true);
    } else if (creationType === "practiceProject") {
      navigate("/practice-projects/create");
    } else {
      navigate(`${paths.createProject}?mode=create`);
    }
  };
  const [creationType, setCreationType] = useState<
    "newProject" | "useTemplate" | "practiceProject"
  >("newProject");
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  // Practice Projects filter states
  const [practiceSearchQuery, setPracticeSearchQuery] = useState("");
  const [practiceStatusFilter, setPracticeStatusFilter] = useState<
    string | undefined
  >();
  const [practiceDifficultyFilter, setPracticeDifficultyFilter] = useState<
    string | undefined
  >();

  const { data: courses = [], isLoading: coursesLoading } = useLTIContextsQuery(
    { enabled: true },
  );
  const typedCourses = courses as LTIContext[];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCourseId = searchParams.get("courseId");
    const urlResourceLinkId = searchParams.get("resourceLinkId");

    if (urlCourseId && urlResourceLinkId) {
      setProjectLinkParams(urlCourseId, urlResourceLinkId);
    }
  }, [location.search, setProjectLinkParams]);

  const projectQueryParams: GetEnhancedProjectsParams = {
    page,
    limit,
    ...(searchQuery ? { search: searchQuery } : {}),
    ...(courseFilter ? { courseId: courseFilter } : {}),
    ...(statusFilter ? { status: statusFilter.toLowerCase() as any } : {}),
    ...(createdBy ? { createdBy: createdBy as any } : {}),
    ...(difficultyFilter ? { difficultyLevel: difficultyFilter as any } : {}),
    ...(isLinkedFilter && isLinkedFilter !== "all"
      ? { isLinked: isLinkedFilter === "linked" }
      : {}),
    ...(typeFilter ? { type: typeFilter as any } : {}),
  };

  const {
    data: enhancedProjectsData,
    isLoading: projectsLoading,
    isError: projectsError,
    refetch: refetchProjects,
  } = useEnhancedProjectsQuery(projectQueryParams as GetEnhancedProjectsParams);

  // Extract projects and pagination from the API response
  const projects = enhancedProjectsData?.projects || [];
  const pagination = enhancedProjectsData?.pagination || {};

  const { mutate: deleteTemplateMutation, isPending: isDeletingTemplate } =
    useDeleteProjectTemplateMutation({
      onSuccess: () => {
        toast.success("Template deleted successfully");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Failed to delete template",
        );
      },
    });

  // Practice Projects queries
  const { data: practiceProjectsStats } = usePracticeProjectStatsQuery();
  const {
    data: practiceProjectsData,
    isLoading: practiceProjectsLoading,
    refetch: refetchPracticeProjects,
  } = usePracticeProjectsQuery(
    {
      page,
      limit,
      status: activeTab === "practice-projects" ? undefined : undefined,
    },
    {
      enabled: activeTab === "practice-projects",
    },
  );

  const practiceProjects = practiceProjectsData?.data?.projects || [];
  const practiceProjectsPagination = practiceProjectsData?.data?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  };

  const { mutate: deletePracticeProject } = useDeletePracticeProjectMutation();
  const { mutate: duplicatePracticeProject } =
    useDuplicatePracticeProjectMutation();
  const { mutate: publishPracticeProject } =
    usePublishPracticeProjectMutation();
  const { mutate: unpublishPracticeProject } =
    useUnpublishPracticeProjectMutation();

  const handleDeleteTemplate = (templateId: string) => {
    deleteTemplateMutation(templateId);
  };

  const [inputValue, setInputValue] = useState("");

  // Debounce the input value for API calls
  const debouncedSearchQuery = useDebounce(inputValue, 1000);

  // Sync searchQuery with debounced value
  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
    setPage(1);
  }, [debouncedSearchQuery]);

  // Sync inputValue when searchQuery changes externally
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  interface TemplateFormData {
    title: string;
    description: string;
    category: string;
    subcategory: string;
    skills_covered: string[];
    technologies_used: string[];
  }

  interface ConvertToTemplateDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    projectId?: string | number;
    refetchProjects: () => void;
  }

  const ConvertToTemplateDialog = ({
    isOpen,
    onOpenChange,
    projectId,
  }: ConvertToTemplateDialogProps) => {
    const [formData, setFormData] = useState<TemplateFormData>({
      title: "",
      description: "",
      category: "",
      subcategory: "",
      skills_covered: [],
      technologies_used: [],
    });
    const [skillInput, setSkillInput] = useState("");
    const [techInput, setTechInput] = useState("");

    const handleAddSkill = () => {
      if (
        skillInput.trim() &&
        !formData.skills_covered.includes(skillInput.trim())
      ) {
        setFormData({
          ...formData,
          skills_covered: [...formData.skills_covered, skillInput.trim()],
        });
        setSkillInput("");
      }
    };

    const handleRemoveSkill = (skill: string) => {
      setFormData({
        ...formData,
        skills_covered: formData.skills_covered.filter((s) => s !== skill),
      });
    };

    const handleAddTech = () => {
      if (
        techInput.trim() &&
        !formData.technologies_used.includes(techInput.trim())
      ) {
        setFormData({
          ...formData,
          technologies_used: [...formData.technologies_used, techInput.trim()],
        });
        setTechInput("");
      }
    };

    const handleRemoveTech = (tech: string) => {
      setFormData({
        ...formData,
        technologies_used: formData.technologies_used.filter((t) => t !== tech),
      });
    };

    const handleSubmit = async () => {
      if (!isFormValid()) return;

      try {
        const response = await mutateAsync({
          projectId: projectId as string,
          body: formData,
        });
        if (response.isSuccess) {
          toast.success("Project converted to template successfully!");
          refetchProjects();
        }
        onOpenChange(false);

        // Reset form
        setFormData({
          title: "",
          description: "",
          category: "",
          subcategory: "",
          skills_covered: [],
          technologies_used: [],
        });
        setSkillInput("");
        setTechInput("");
      } catch (error: any) {
        toast.error((error as any)?.response?.data?.data?.message);
      }
    };

    const handleCancel = () => {
      onOpenChange(false);
      // Reset form
      setFormData({
        title: "",
        description: "",
        category: "",
        subcategory: "",
        skills_covered: [],
        technologies_used: [],
      });
      setSkillInput("");
      setTechInput("");
    };

    const isFormValid = () => {
      return (
        formData.title.trim() !== "" &&
        formData.description.trim() !== "" &&
        formData.category.trim() !== "" &&
        formData.subcategory.trim() !== "" &&
        formData.skills_covered.length > 0 &&
        formData.technologies_used.length > 0
      );
    };

    if (!isOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(156, 163, 175, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          overflow: "hidden",
        }}
        onClick={handleCancel}
        tabIndex={1000}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "42rem",
            margin: "1rem",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="rounded-lg  border"
            style={{
              maxHeight: "90vh",
              overflowY: "auto",
              backgroundColor: "#ffffff",
            }}
          >
            <div className="p-6">
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Convert Project to Template
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Fill in the details to create a template from this project.
                </p>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="e.g., Data Analysis Template"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    autoComplete="off"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Describe what this template is for..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                  />
                </div>

                {/* Category & Subcategory */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">
                      Category <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="category"
                      placeholder="e.g., Data Science"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subcategory">
                      Subcategory <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="subcategory"
                      placeholder="e.g., Analysis"
                      value={formData.subcategory}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subcategory: e.target.value,
                        })
                      }
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Skills Covered */}
                <div className="space-y-2">
                  <Label htmlFor="skills">
                    Skills Covered <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="skills"
                      placeholder="Add a skill (e.g., Python)"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      onClick={handleAddSkill}
                      className="bg-bits-blue text-white hover:bg-bits-blue-700"
                    >
                      Add
                    </Button>
                  </div>
                  {formData.skills_covered.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.skills_covered.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                        >
                          {skill}
                          <button
                            onClick={() => handleRemoveSkill(skill)}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Technologies Used */}
                <div className="space-y-2">
                  <Label htmlFor="technologies">
                    Technologies Used <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="technologies"
                      placeholder="Add a technology (e.g., Jupyter)"
                      value={techInput}
                      onChange={(e) => setTechInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTech();
                        }
                      }}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      onClick={handleAddTech}
                      className="bg-bits-blue text-white hover:bg-bits-blue-700"
                    >
                      Add
                    </Button>
                  </div>
                  {formData.technologies_used.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.technologies_used.map((tech, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                        >
                          {tech}
                          <button
                            onClick={() => handleRemoveTech(tech)}
                            className="hover:bg-green-200 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="flex gap-2 mt-6">
                <Button
                  onClick={handleCancel}
                  className="flex-1"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!isFormValid()}
                  type="submit"
                  onClick={handleSubmit}
                  className="flex-1 bg-bits-blue hover:bg-bits-blue"
                >
                  Create Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const {
    data: courseStaff,
    isLoading: staffLoading,
    isError: staffError,
    error: staffErrorObj,
    refetch,
  } = useStaffMemberQuery(courseFilter || "", Boolean(courseFilter));
  useEffect(() => {
    if (courseFilter) refetch();
  }, [courseFilter]);
  const staffData = [
    ...(courseStaff?.instructors || []),
    ...(courseStaff?.tas || []),
  ];

  // Column definitions for MaterialTable
  const columns: MRT_ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Name",
        Cell: ({ row }) => {
          const title = row.original.title as string;
          const projectId = row.original.projectId as string;
          const displayText = `${title}`;
          const shouldShowTooltip = displayText && displayText.length > 40;

          const content = (
            <div className="flex flex-col">
              <div className="max-w-[250px] truncate cursor-help text-black">
                {displayText}
              </div>
              <div className="text-gray-500 text-xs mt-1">
                Project ID - {projectId}
              </div>
            </div>
          );

          if (shouldShowTooltip) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent>
                  <p>{displayText}</p>
                  <p className="text-xs text-gray-400">
                    Project ID - {projectId}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          }

          return content;
        },
      },
      {
        accessorKey: "course.name",
        header: "Course",
        Cell: ({ cell }) => (
          <span className="text-black">{String(cell.getValue() ?? "")}</span>
        ),
      },
      {
        accessorKey: "ltiResourceLinks",
        header: "LTI Reference",
        Cell: ({ row }) => {
          const ltiLinks = row.original.ltiResourceLinks || [];
          return (
            <span className="text-black text-center">
              {ltiLinks.length > 0 ? `${ltiLinks[0].resourceLinkTitle}` : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "createdBy.name",
        header: "Created By",
        Cell: ({ cell }) => (
          <span className="capitalize text-black">
            {String(cell.getValue() ?? "N/A")}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const status = String(cell.getValue() ?? "").toLowerCase();

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="capitalize text-black cursor-default">
                  {getStatusBadge(status)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {status === "active"
                    ? "This project is currently active and accepting submissions."
                    : status === "draft"
                      ? "This project is in draft mode and not yet published."
                      : status === "closed"
                        ? "This project has been closed explicitly by the instructor or the creator."
                        : status === "deadline passed"
                          ? "The due date for this project has passed and not accepting submissions."
                          : "Status information unavailable."}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "difficultyLevel",
        header: "Difficulty",
        Cell: ({ cell }) => (
          <span className="text-black">
            {getDifficultyBadge(String(cell.getValue() ?? ""))}
          </span>
        ),
      },
      {
        accessorKey: "submissionCount",
        header: "Submissions",
        Cell: ({ cell }) => (
          <div className="flex items-center justify-center w-10 text-sm text-black bg-bits-grey-100 rounded-full">
            {String(cell.getValue() ?? "")}
          </div>
        ),
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        Cell: ({ cell }) => (
          <span className="text-sm text-black">
            {String(cell.getValue() ?? "-")}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,

        Cell: ({ row }) => {
          const project = row.original;
          return (
            <div className="flex items-center gap-2 action-buttons">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      navigate(
                        paths.instructorProjectDetails.replace(
                          ":projectId",
                          project.id,
                        ),
                      );
                    }}
                  >
                    <Eye className="h-6 w-6 text-gray-600 hover:text-gray-800" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View project details</p>
                </TooltipContent>
              </Tooltip>
              {user?.role !== "ta" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={
                          project.isLinked === true ||
                          project.status !== "draft"
                        }
                        onClick={() => {
                          setSelectedProjectId(project.id);
                          setSelectedCreatedBy(project?.createdBy?.id);
                          setIsModalOpen(true);
                        }}
                      >
                        <Link className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {project.isLinked
                          ? "Project already linked"
                          : "Link project to LTI"}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => duplicateProjectMutation(project.id)}
                      >
                        <FileStack className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Duplicate project</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setDeleteProjectId(project.id)}
                      >
                        <Trash2 className="h-5 w-5 text-black" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete project</p>
                    </TooltipContent>
                  </Tooltip>

                  {deleteProjectId && (
                    <DeleteProjectModal
                      isOpen={!!deleteProjectId}
                      refetchProjects={refetchProjects}
                      onClose={() => setDeleteProjectId(null)}
                      projectId={deleteProjectId}
                      projectName={
                        projects.find((p) => p.id === deleteProjectId)?.title ??
                        "Untitled Project"
                      }
                    />
                  )}

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${
                          project.status.toLowerCase() === "active"
                            ? "opacity-50"
                            : ""
                        }`}
                        onClick={() => {
                          if (project.status.toLowerCase() === "active") {
                            toast.error("Published projects cannot be edited.");
                            return;
                          }

                          navigate(
                            `${paths.editProject.replace(
                              ":projectId",
                              project.id,
                            )}?mode=edit`,
                          );
                        }}
                      >
                        <Edit className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit project</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setTemplateDialogProjectId(project.id)}
                      >
                        <FileSymlink className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Convert to Template</p>
                    </TooltipContent>
                  </Tooltip>
                  {project.status.toLowerCase() !== "closed" && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            setCloseProjectId(project.id);
                          }}
                        >
                          <Ban className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Close Project</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {closeProjectId && (
                    <CloseProjectModal
                      isOpen={!!closeProjectId}
                      refetchProjects={refetchProjects}
                      onClose={() => setCloseProjectId(null)}
                      projectId={closeProjectId}
                      projectName={
                        projects.find((p) => p.id === closeProjectId)?.title ??
                        "Untitled Project"
                      }
                    />
                  )}
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      navigate,
      user?.role,
      isModalOpen,
      selectedProjectIdToLink,
      refetchProjects,
      duplicateProjectMutation,
      deleteProjectId,
      projects,
      templateDialogProjectId,
      closeProjectId,
    ],
  );

  interface AllProjectsTabProps {
    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
    projects: any;
    pagination: any;
    projectsLoading: boolean;
    projectsError: any;
    refetchProjects: () => void;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    limit: number;
    setLimit: React.Dispatch<React.SetStateAction<number>>;
    courseFilter: string | undefined;
    setCourseFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    statusFilter: string | undefined;
    setStatusFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    difficultyFilter: string | undefined;
    setDifficultyFilter: React.Dispatch<
      React.SetStateAction<string | undefined>
    >;
    isLinkedFilter: string | undefined;
    setIsLinkedFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    typeFilter: string | undefined;
    setTypeFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
    createdBy: string | number | undefined;
    setCreatedBy: React.Dispatch<
      React.SetStateAction<string | number | undefined>
    >;
    staffLoading: boolean;
    courseStaff: any;
    staffData: any;
    inputValue: string;
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    typedCourses: LTIContext[];
    coursesLoading: boolean;
    user: any;
    navigate: any;
  }

  const renderAllProjectsTab = useCallback(
    ({
      searchQuery,
      setSearchQuery,
      projects,
      pagination,
      projectsLoading,
      projectsError,
      refetchProjects,
      page,
      setPage,
      limit,
      setLimit,
      courseFilter,
      setCourseFilter,
      statusFilter,
      setStatusFilter,
      difficultyFilter,
      setDifficultyFilter,
      isLinkedFilter,
      setIsLinkedFilter,
      typeFilter,
      setTypeFilter,
      staffLoading,
      courseStaff,
      staffData,
      createdBy,
    }: AllProjectsTabProps) => {
      // const handleDelete = async () => {
      //   // Simulate API call
      //   await new Promise((resolve) => setTimeout(resolve, 2000));

      //   setIsDeleteModalOpen(false);
      //   alert("Project deleted successfully!");
      // };
      return (
        <div className="space-y-6">
          <Card className="border">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div className="flex flex-wrap gap-4">
                  <div className="relative flex-1 min-w-40 w-80">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search Projects..."
                      className="pl-10"
                      value={inputValue}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // ✅ Prevent form submission
                        }
                      }}
                    />
                  </div>

                  <Select
                    value={courseFilter || "all-courses"}
                    onValueChange={(v) => {
                      setCourseFilter(v === "all-courses" ? undefined : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-auto sm:min-w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="text-md ">
                      <SelectItem value="all-courses">All courses</SelectItem>
                      {coursesLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading courses...
                        </SelectItem>
                      ) : (
                        typedCourses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.contextTitle}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Select
                    value={createdBy ? String(createdBy) : "all-courses"}
                    onValueChange={(v) => {
                      setCreatedBy(v === "all-courses" ? undefined : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="text-md ">
                      <SelectItem value="all-courses">
                        Select Instructors
                      </SelectItem>
                      {!courseFilter ? (
                        <SelectItem value="loading" disabled>
                          Please select a course
                        </SelectItem>
                      ) : staffLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading courses...
                        </SelectItem>
                      ) : (
                        staffData?.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Select
                    value={isLinkedFilter || "all"}
                    onValueChange={(v) => {
                      setIsLinkedFilter(v === "all" ? undefined : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All projects</SelectItem>
                      <SelectItem value="linked">Linked projects</SelectItem>
                      <SelectItem value="unlinked">
                        Unlinked projects
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={statusFilter || "all-status"}
                    onValueChange={(v) => {
                      setStatusFilter(v === "all-status" ? undefined : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-status">All status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={difficultyFilter || "all-difficulties"}
                    onValueChange={(v) => {
                      setDifficultyFilter(
                        v === "all-difficulties" ? undefined : v,
                      );
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-difficulties">
                        All Difficulties
                      </SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={typeFilter || "all-types"}
                    onValueChange={(v) => {
                      setTypeFilter(v === "all-types" ? undefined : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all-types">All Types</SelectItem>
                      <SelectItem value="group">Group</SelectItem>
                      <SelectItem value="individual">Individual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <MaterialTable
            columns={columns}
            data={projects || []}
            isLoading={projectsLoading}
            error={projectsError}
            emptyMessage={
              projectsLoading
                ? "Loading projects..."
                : projectsError
                  ? "Failed to load projects"
                  : "No projects found"
            }
            enableColumnPinning={true}
            stickyColumns={{ right: ["actions"] }}
          />

          {/* Pagination */}
          {pagination && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-bits-grey-600">
                Page {pagination.currentPage} of {pagination.totalPages} •{" "}
                {pagination.totalItems} items
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || projectsLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages || projectsLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    setLimit(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 30, 50].map((l) => (
                      <SelectItem key={l} value={String(l)}>
                        {l} / page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      inputValue,
      handleSearchChange,
      typedCourses,
      coursesLoading,
      courseFilter,
      statusFilter,
      difficultyFilter,
      isLinkedFilter,
      typeFilter,
      staffLoading,
      staffData,
      createdBy,
      projects,
      projectsLoading,
      projectsError,
      pagination,
      page,
      limit,
      user,
      navigate,
    ],
  );

  const renderPracticeProjectsTab = useCallback(() => {
    const practiceColumns: MRT_ColumnDef<any>[] = [
      {
        header: "Title",
        accessorKey: "title",
        Cell: ({ row }: any) => {
          const project = row.original;
          const title =
            typeof project.title === "string"
              ? project.title
              : JSON.stringify(project.title);
          const projectId = project.project_id_display;
          const displayText = title;
          const shouldShowTooltip = displayText && displayText.length > 40;

          const content = (
            <div className="flex flex-col">
              <div className="max-w-[250px] truncate cursor-help text-black font-medium">
                {displayText}
              </div>
              <div className="text-gray-500 text-xs mt-1">
                Project ID - {projectId || "N/A"}
              </div>
            </div>
          );

          if (shouldShowTooltip) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent className="max-w-md p-3">
                  <p className="text-sm">{displayText}</p>
                </TooltipContent>
              </Tooltip>
            );
          }

          return content;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        Cell: ({ row }: any) => {
          const status = row.original.status;
          return (
            <Badge
              variant={status === "published" ? "default" : "secondary"}
              className="capitalize"
            >
              {status}
            </Badge>
          );
        },
      },
      {
        header: "Difficulty",
        accessorKey: "difficulty_level",
        Cell: ({ row }: any) => (
          <Badge variant="outline" className="capitalize">
            {row.original.difficulty_level || "N/A"}
          </Badge>
        ),
      },
      {
        header: "Enrollments",
        accessorKey: "enrollmentCount",
        Cell: ({ row }: any) => (
          <div className="text-center">{row.original.enrollmentCount || 0}</div>
        ),
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        Cell: ({ row }: any) => (
          <div className="text-sm text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </div>
        ),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableSorting: false,
        Cell: ({ row }: any) => {
          const project = row.original;
          return (
            <div className="flex items-center gap-2 action-buttons">
              {/* 1. View Project Details */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => navigate(`/practice-projects/${project.id}`)}
                  >
                    <Eye className="h-6 w-6 text-gray-600 hover:text-gray-800" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View project details</p>
                </TooltipContent>
              </Tooltip>

              {/* 2. Publish / Unpublish */}
              {project.status === "draft" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => publishPracticeProject(project.id)}
                    >
                      <CheckCircle className="h-5 w-5 text-green-600 hover:text-green-700" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Publish project</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {project.status === "published" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setUnpublishPracticeProjectId(project.id)}
                    >
                      <XCircle className="h-5 w-5 text-orange-600 hover:text-orange-700" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Unpublish project</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* 3. Edit Project */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      if (project.status === "published") {
                        toast.error("Unpublish to edit the project");
                      } else {
                        navigate(`/practice-projects/edit/${project.id}`);
                      }
                    }}
                  >
                    <Edit className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit project</p>
                </TooltipContent>
              </Tooltip>

              {/* 4. Duplicate Project */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => {
                      duplicatePracticeProject(project.id);
                    }}
                  >
                    <Copy className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Duplicate project</p>
                </TooltipContent>
              </Tooltip>

              {/* 5. Delete Project */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setDeletePracticeProjectId(project.id)}
                  >
                    <Trash2 className="h-5 w-5 text-black" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete project</p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      },
    ];

    return (
      <div className="space-y-6">
        <Card className="border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-40 w-80">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search Practice Projects..."
                    className="pl-10"
                    value={practiceSearchQuery}
                    onChange={(e) => setPracticeSearchQuery(e.target.value)}
                  />
                </div>
                <Select
                  value={practiceStatusFilter || "all-status"}
                  onValueChange={(v) => {
                    setPracticeStatusFilter(v === "all-status" ? undefined : v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-status">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={practiceDifficultyFilter || "all-difficulties"}
                  onValueChange={(v) => {
                    setPracticeDifficultyFilter(
                      v === "all-difficulties" ? undefined : v,
                    );
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-difficulties">
                      All Difficulties
                    </SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <MaterialTable
          columns={practiceColumns}
          data={practiceProjects || []}
          isLoading={practiceProjectsLoading}
          emptyMessage={
            practiceProjectsLoading
              ? "Loading practice projects..."
              : "No practice projects found"
          }
          enableColumnPinning={true}
          stickyColumns={{ right: ["actions"] }}
        />

        {/* Pagination */}
        {practiceProjectsPagination?.currentPage && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-bits-grey-600">
              Page {practiceProjectsPagination.currentPage} of{" "}
              {practiceProjectsPagination.totalPages} •{" "}
              {practiceProjectsPagination.totalItems} items
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || practiceProjectsLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={
                  page >= practiceProjectsPagination.totalPages ||
                  practiceProjectsLoading
                }
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  setLimit(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((l) => (
                    <SelectItem key={l} value={String(l)}>
                      {l} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Delete Practice Project Modal */}
        {deletePracticeProjectId && (
          <DeletePracticeProjectModal
            isOpen={!!deletePracticeProjectId}
            onClose={() => setDeletePracticeProjectId(null)}
            refetchProjects={refetchPracticeProjects}
            projectId={deletePracticeProjectId}
            projectName={
              practiceProjects.find((p) => p.id === deletePracticeProjectId)
                ?.title ?? "Untitled Practice Project"
            }
          />
        )}

        {/* Unpublish Practice Project Modal */}
        {unpublishPracticeProjectId && (
          <UnpublishPracticeProjectModal
            isOpen={!!unpublishPracticeProjectId}
            onClose={() => setUnpublishPracticeProjectId(null)}
            onConfirm={() => {
              unpublishPracticeProject(unpublishPracticeProjectId);
              setUnpublishPracticeProjectId(null);
            }}
            projectName={
              practiceProjects.find((p) => p.id === unpublishPracticeProjectId)
                ?.title ?? "Untitled Practice Project"
            }
          />
        )}
      </div>
    );
  }, [
    practiceProjects,
    practiceProjectsLoading,
    practiceProjectsData,
    practiceProjectsPagination,
    page,
    limit,
    practiceSearchQuery,
    practiceStatusFilter,
    practiceDifficultyFilter,
    deletePracticeProject,
    publishPracticeProject,
    unpublishPracticeProject,
    deletePracticeProjectId,
    unpublishPracticeProjectId,
    navigate,
  ]);

  const TemplatesTab = () => {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [templateSearchQuery, setTemplateSearchQuery] = useState("");
    const [templateDifficultyFilter, setTemplateDifficultyFilter] = useState<
      string | undefined
    >(undefined);
    const [templateCategoryFilter, setTemplateCategoryFilter] = useState<
      string | undefined
    >(undefined);
    const [templateSubcategoryFilter, setTemplateSubcategoryFilter] = useState<
      string | undefined
    >(undefined);
    const [templateInputValue, setTemplateInputValue] = useState("");

    // Categories and subcategories are not available, using empty arrays
    const uniqueCategories: string[] = [];
    const uniqueSubcategories: string[] = [];

    // Debounce the template search input
    const debouncedTemplateSearch = useDebounce(templateInputValue, 1000);

    // Sync search query with debounced value
    useEffect(() => {
      setTemplateSearchQuery(debouncedTemplateSearch);
      setPage(1);
    }, [debouncedTemplateSearch]);

    // Sync input value when search query changes externally
    useEffect(() => {
      setTemplateInputValue(templateSearchQuery);
    }, [templateSearchQuery]);

    // Handle template search input changes
    const handleTemplateSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTemplateInputValue(e.target.value);
      },
      [],
    );

    // Update params to use state values
    const params: GetTemplatesParams = useMemo(
      () => ({
        page,
        limit,
        ...(templateSearchQuery ? { search: templateSearchQuery } : {}),
        ...(templateDifficultyFilter
          ? { difficultyLevel: templateDifficultyFilter as any }
          : {}),
        ...(templateCategoryFilter ? { category: templateCategoryFilter } : {}),
        ...(templateSubcategoryFilter
          ? { subcategory: templateSubcategoryFilter }
          : {}),
      }),
      [
        page,
        limit,
        templateSearchQuery,
        templateDifficultyFilter,
        templateCategoryFilter,
        templateSubcategoryFilter,
      ],
    );

    const {
      data: templateData,
      isLoading,
      isError,
      error,
    } = useProjectTemplatesQuery(params);

    // Extract pagination data
    const pagination = templateData
      ? {
          page: templateData?.page,
          pages: Math.ceil(templateData?.total / templateData?.limit),
          total: templateData?.total,
        }
      : null;
    console.log(templateData);
    return (
      <div className="space-y-6">
        {/* Search and Filters */}
        <Card className="border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <h2 className="text-lg font-medium text-bits-grey">
                Project Templates
              </h2>
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-40 w-80">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search Templates..."
                    className="pl-10"
                    value={templateInputValue}
                    onChange={handleTemplateSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
                <Select
                  value={templateCategoryFilter || "all-categories"}
                  onValueChange={(v) => {
                    setTemplateCategoryFilter(
                      v === "all-categories" ? undefined : v,
                    );
                    setTemplateSubcategoryFilter(undefined); // Reset subcategory when category changes
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-categories">
                      All Categories
                    </SelectItem>
                    {uniqueCategories.map((category) => (
                      <SelectItem
                        key={String(category)}
                        value={String(category)}
                      >
                        {String(category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={templateSubcategoryFilter || "all-subcategories"}
                  onValueChange={(v) => {
                    setTemplateSubcategoryFilter(
                      v === "all-subcategories" ? undefined : v,
                    );
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Subcategories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-subcategories">
                      All Subcategories
                    </SelectItem>
                    {uniqueSubcategories.map((subcategory) => (
                      <SelectItem
                        key={subcategory || "undefined"}
                        value={subcategory || ""}
                      >
                        {subcategory || "Undefined"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={templateDifficultyFilter || "all-difficulties"}
                  onValueChange={(v) => {
                    setTemplateDifficultyFilter(
                      v === "all-difficulties" ? undefined : v,
                    );
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-difficulties">
                      All Difficulties
                    </SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        <Card className="p-4">
          {isLoading ? (
            <div className="text-center py-12 text-bits-grey-600">
              Loading templates...
            </div>
          ) : isError ? (
            <div className="text-center py-12 text-bits-red text-sm">
              Failed to load templates
            </div>
          ) : !templateData?.items || templateData.items.length === 0 ? (
            <div className="text-center py-12 text-bits-grey-600 text-sm">
              No templates found
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              {templateData?.items?.map((template) => (
                <Card
                  key={template.id}
                  className="bg-white border border-gray-200"
                >
                  <CardContent className="p-6">
                    {/* Three Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      {/* Column 1 - Title and Details (5 cols) */}
                      <div className="lg:col-span-5 space-y-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-medium">
                            {template.template_name}
                          </h3>
                          {getStatusBadge("active")}
                        </div>

                        <p className="text-sm text-bits-grey-600">
                          {template.description}
                        </p>

                        <div className="flex flex-col gap-2 text-sm text-bits-grey-600">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-gray-600 hover:text-gray-800" />
                            <span>Created by {template.createdBy.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-gray-600 hover:text-gray-800" />
                            <span>
                              Last used:{" "}
                              {template?.last_used
                                ? new Date(
                                    template.last_used,
                                  ).toLocaleDateString()
                                : "-"}
                            </span>
                          </div>
                          {template.usage_count > 0 && (
                            <div className="text-body-sm font-medium text-bits-grey-600">
                              Used {template.usage_count} times
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Column 2 - Tags and Template Details (5 cols) */}
                      <div className="lg:col-span-5 space-y-4 border-l border-r px-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-semibold text-bits-grey-700 mb-1">
                              Category
                            </p>
                            <p className="text-sm text-bits-grey-600">
                              {template.category || "Not specified"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-bits-grey-700 mb-1">
                              Subcategory
                            </p>
                            <p className="text-sm text-bits-grey-600">
                              {template.subcategory || "Not specified"}
                            </p>
                          </div>
                        </div>

                        {template.skills_covered &&
                          template.skills_covered.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-bits-grey-700 mb-2">
                                Skills Covered
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {template.skills_covered.map((skill, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-blue-50 text-bits-blue-800 text-xs rounded-full border border-blue-200"
                                  >
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                        {template.technologies_used &&
                          template.technologies_used.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-bits-grey-700 mb-2">
                                Technologies Used
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {template.technologies_used.map(
                                  (tech, index) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full border border-indigo-200"
                                    >
                                      {tech}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Column 3 - Action Buttons (2 cols) */}
                      <div className="lg:col-span-2 flex flex-col justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigate(
                              `${paths.instructorProjectDetails.replace(
                                ":projectId",
                                template.id,
                              )}?mode=template`,
                            );
                          }}
                          className="text-bits-grey-700 w-full justify-start"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            console.log("Edit clicked");
                            console.log(template);
                            navigate(
                              `${paths.editProject.replace(
                                ":projectId",
                                template.id,
                              )}?mode=template&action=edit`,
                            );
                          }}
                          className="text-bits-grey-700 w-full justify-start"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id)}
                          disabled={isDeletingTemplate}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-start"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {isDeletingTemplate ? "Deleting..." : "Delete"}
                        </Button>
                        <Button
                          className="bg-bits-blue w-full"
                          size="sm"
                          onClick={() => handleUseTemplate(template.id)}
                        >
                          Use Template
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </Card>
        {/* Pagination */}
        {pagination && (
          <div className="flex items-center justify-between pt-2 mt-4 w-full">
            <div className="text-sm text-bits-grey-600">
              Page {pagination.page} of {pagination.pages} • {pagination.total}{" "}
              items
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.pages || isLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  setLimit(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((l) => (
                    <SelectItem key={l} value={String(l)}>
                      {l} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Empty State */}
        {/* {filteredTemplates.length === 0 && (
        <Card className="bg-white border border-gray-200">
          <CardContent className="p-12 text-center">
            <Bookmark className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? "No templates found" : "No templates yet"}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery
                ? "Try adjusting your search or filter criteria."
                : "Create your first project template to reuse configurations."}
            </p>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => navigateTo("create-project")}
            >
              <Plus className="h-4 w-4 mr-2 text-gray-600 hover:text-gray-800" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      )} */}
      </div>
    );
  };
  const AnalyticsTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-white border border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center font-medium gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-bits-blue" />
            <span className="text-lg font-medium text-bits-grey">
              Project Completion Trends
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-bits-green mb-2">85%</div>
            <div className="text-sm text-bits-grey-600">
              Average completion rate
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">This Month</span>
              <span className="text-sm font-normal text-bits-grey-600">
                92%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Last Month</span>
              <span className="text-sm font-normal text-bits-grey-600">
                88%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">3 Months Ago</span>
              <span className="text-sm font-normal text-bits-grey-600">
                76%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center font-medium gap-2 text-lg">
            <Layers className="h-5 w-5 text-bits-warning" />
            <span className="text-lg font-medium text-bits-grey">
              Popular Technologies
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Python */}
            <div className="flex justify-between items-center">
              <span className="text-md font-normal text-bits-grey-600">
                Python
              </span>
              <div className="flex items-center gap-2 w-[40%]">
                <Progress
                  value={95}
                  className="h-2 text-xs font-normal text-bits-grey-600"
                />
                <span className="text-sm text-bits-grey-600">95%</span>
              </div>
            </div>

            {/* Scikit-learn */}
            <div className="flex justify-between items-center">
              <span className="text-md font-normal text-bits-grey-600">
                Scikit-learn
              </span>
              <div className="flex items-center gap-2 w-[40%]">
                <Progress
                  value={85}
                  className="h-2 text-xs font-normal text-bits-grey-600"
                />
                <span className="text-sm text-bits-grey-600">85%</span>
              </div>
            </div>

            {/* Pandas */}
            <div className="flex justify-between items-center">
              <span className="text-md font-normal text-bits-grey-600">
                Pandas
              </span>
              <div className="flex items-center gap-2 w-[40%]">
                <Progress
                  value={92}
                  className="h-2 text-xs font-normal text-bits-grey-600"
                />
                <span className="text-sm text-bits-grey-600">92%</span>
              </div>
            </div>

            {/* TensorFlow */}
            <div className="flex justify-between items-center">
              <span className="text-md font-normal text-bits-grey-600">
                TensorFlow
              </span>
              <div className="flex items-center gap-2 w-[40%]">
                <Progress
                  value={68}
                  className="h-2 text-xs font-normal text-bits-grey-600"
                />
                <span className="text-sm text-bits-grey-600">68%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2 bg-white border border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center font-medium gap-2 text-lg">
            <Clock className="h-5 w-5 text-bits-blue" />
            <span className="text-lg font-medium text-bits-grey">
              Recent Activity
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-bits-green-success-25">
              <div className="mt-1">
                <CheckCircle className="h-4 w-4 text-bits-green" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div className="">
                    <div className="text-md font-medium">Project completed</div>
                    <div className="text-sm font-normal text-bits-grey-600 mt-1">
                      Neural Networks Implementation - 20/20 students submitted
                    </div>
                  </div>
                  <span className="text-xs text-bits-grey-600">
                    2 hours ago
                  </span>
                </div>
              </div>
            </div>

            {/* <div className="flex items-start gap-3 p-3 rounded-lg ">
              <div className="mt-1">
                <Plus className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-medium text-gray-900">New Mall Customers projects created</div>
                    <div className="text-sm text-gray-600 mt-1">Dr. A. Sharma created 3-part Mall Customers ML series</div>
                  </div>
                  <span className="text-xs text-bits-grey-600">1 day ago</span>
                </div>
              </div>
            </div> */}

            <div className="flex items-start gap-3 p-3 rounded-lg bg-bits-warning-50 ">
              <div className="mt-1">
                <AlertTriangle className="h-4 w-4 text-bits-orange" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-md font-medium">
                      Deadline approaching
                    </div>
                    <div className="text-sm font-normal text-bits-grey-600 mt-1">
                      Mall Customers Part 1 due in 5 days - 42/55 submitted
                    </div>
                  </div>
                  <span className="text-xs text-bits-grey-600">
                    3 hours ago
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-bits-blue-50 ">
              <div className="mt-1">
                <Plus className="h-4 w-4 text-bits-blue-500" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-md font-medium">
                      New template created
                    </div>
                    <div className="text-sm font-normal text-bits-grey-600 mt-1">
                      Dr. A. Sharma created "Advanced NLP Pipeline"
                    </div>
                  </div>
                  <span className="text-xs text-bits-grey-600">2 days ago</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  interface ShowSelectTemplateModalProps {
    isOpenSelectTemplate: boolean;
    setIsSelectTemplate: (open: boolean) => void;
    setTrackSelectTemplate: (val: any) => void;
  }

  const ShowSelectTemplateModal = ({
    isOpenSelectTemplate,
    setIsSelectTemplate,
    setTrackSelectTemplate,
  }: ShowSelectTemplateModalProps) => {
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(
      null,
    );

    return (
      <Dialog open={isOpenSelectTemplate} onOpenChange={setIsSelectTemplate}>
        <DialogContent className="sm:max-w-2xl bg-white border">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Select a Template
            </DialogTitle>
            <DialogDescription>
              Create a project from scratch or get started faster with a
              template.
            </DialogDescription>
          </DialogHeader>

          {/* Search & Sort */}
          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-600 hover:text-gray-800" />
              <Input placeholder="Search project template" className="pl-10" />
            </div>
            <Select defaultValue="new-first">
              <SelectTrigger className="w-40 flex-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new-first">Newest First</SelectItem>
                <SelectItem value="later">Later</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Template Cards */}
          <Card className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
            {templates?.map((i, index) => (
              <Card
                key={index}
                onClick={() => setSelectedTemplate(index)}
                className={`p-3 cursor-pointer transition border 
                ${
                  selectedTemplate === index
                    ? "border-bits-blue"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-5">
                  <FileText className="h-5 w-5 text-bits-blue" />
                  <div className="flex-col">
                    <CardTitle className="font-medium text-base">
                      {i.name}
                    </CardTitle>
                    <p className="text-sm text-bits-grey-600">
                      Created at {i.createdAt}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </Card>

          {/* Footer Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => setIsSelectTemplate(false)}
              className="flex-1"
              variant="outline"
            >
              Cancel
            </Button>

            <Button
              disabled={selectedTemplate === null}
              onClick={() => {
                setIsSelectTemplate(false);
                setTrackSelectTemplate(false); // optional
              }}
              className="flex-1 bg-bits-blue hover:bg-bits-blue"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  interface CreateNewProjectModalProps {
    isOpen: boolean;
    selectedOption: "newProject" | "useTemplate" | "practiceProject";
    onClose: () => void;
    onOptionChange: Dispatch<
      SetStateAction<"newProject" | "useTemplate" | "practiceProject">
    >;
    onContinue: () => void;
  }

  const CreateNewProjectModal = memo<CreateNewProjectModalProps>(
    ({ isOpen, selectedOption, onClose, onOptionChange, onContinue }) => {
      if (!isOpen) return null;
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-400/20" onClick={onClose} />

          <div className="relative z-10 w-full max-w-lg bg-white rounded-lg shadow-xl p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  Create Project
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Create a project from scratch or get started faster with a
                template.
              </p>
            </div>

            {/* Radio Options */}
            <div className="mb-6">
              <RadioGroup
                value={selectedOption}
                onValueChange={(value) =>
                  onOptionChange(value as "newProject" | "useTemplate")
                }
              >
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="newProject" id="newProject" />
                    <Label
                      htmlFor="newProject"
                      className="text-base font-medium cursor-pointer"
                    >
                      New Project
                    </Label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="useTemplate" id="useTemplate" />
                    <Label
                      htmlFor="useTemplate"
                      className="text-base font-medium cursor-pointer"
                    >
                      Use Template
                    </Label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <RadioGroupItem
                      value="practiceProject"
                      id="practiceProject"
                    />
                    <Label
                      htmlFor="practiceProject"
                      className="text-base font-medium cursor-pointer"
                    >
                      New Practice Project
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={onContinue}
                className="flex-1 bg-bits-blue hover:bg-bits-blue-700"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      );
    },
  );

  CreateNewProjectModal.displayName = "CreateNewProjectModal";

  interface LinkProjectModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    projectId: string;
    platformId?: string;
    createdBy?: string;
    refetchProjects: (
      options?: RefetchOptions | undefined,
    ) => Promise<QueryObserverResult<EnhancedProjectsResponseData, Error>>;
  }

  const LinkProjectModal = ({
    isOpen,
    setIsOpen,
    projectId,
    refetchProjects,
    platformId,
    createdBy,
  }: LinkProjectModalProps) => {
    const { courseId, resourceLinkId, clearProjectLinkParams } =
      useProjectLink();
    const linkProjectMutation = useLinkProjectMutation();

    // State management
    const [selectedCourse, setSelectedCourse] = useState<string>("");
    const [selectedInstructorIds, setSelectedInstructorIds] = useState<
      string[]
    >([]);
    const [selectedTaIds, setSelectedTaIds] = useState<string[]>([]);
    const [selectedAssignment, setSelectedAssignment] = useState<string>("");

    // Auto-populate from URL parameters
    useEffect(() => {
      if (isOpen && courseId) {
        setSelectedCourse(courseId);
      }
    }, [isOpen, courseId]);

    useEffect(() => {
      if (isOpen && resourceLinkId && selectedCourse) {
        setSelectedAssignment(resourceLinkId);
      }
    }, [isOpen, resourceLinkId, selectedCourse]);

    // Fetch courses for linking
    const { data: contextsData, isLoading: contextsLoading } =
      useLTIContextsQuery({ enabled: isOpen, forLinking: true });
    const contexts = Array.isArray(contextsData) ? contextsData : [];

    // Get platform ID from selected course
    const selectedContext = contexts.find((ctx) => ctx.id === selectedCourse);
    const selectedPlatformId = selectedContext?.platformId || platformId;

    // Fetch instructors and TAs for selected course
    const { data: instructorsData = [], isLoading: instructorsLoading } =
      useLTIContextMembersQuery(
        {
          contextId: selectedCourse,
          platformId: selectedPlatformId,
          role: `${ROLES.INSTRUCTOR},${ROLES.TA}`,
        },
        isOpen && !!selectedCourse && !!selectedPlatformId,
      );
    const instructors = Array.isArray(instructorsData) ? instructorsData : [];
    useEffect(() => {
      // 1. Ensure instructorsData is ready and createdBy exists
      if (!createdBy || !Array.isArray(instructorsData)) {
        return;
      }

      // 2. Filter instructors (only those with roleName === "instructor")
      const instructorList = instructorsData.filter(
        (item) => item.roleName === "instructor",
      );

      // 3. Check if createdBy ID exists in the filtered list
      const isCreatedByValidInstructor = instructorList.some(
        (instructor) => instructor.id === createdBy,
      );

      // 4. If valid and not already selected, add it
      if (
        isCreatedByValidInstructor &&
        !selectedInstructorIds.includes(createdBy)
      ) {
        setSelectedInstructorIds((prev) => [...prev, createdBy]);
      }
    }, [instructorsData, createdBy]);

    // Fetch assignments for selected course
    const { data: assignmentsData = [], isLoading: assignmentsLoading } =
      useUnassignedResourceLinksQuery(
        {
          contextId: selectedCourse,
          platformId: selectedPlatformId,
        },
        isOpen && !!selectedCourse && !!selectedPlatformId,
      );
    const assignments = Array.isArray(assignmentsData) ? assignmentsData : [];

    // Separate instructors and TAs
    const instructorList = instructors.filter(
      (m) => m.roleName === "instructor",
    );
    const taList = instructors.filter((m) => m.roleName === "ta");

    // Event handlers
    const handleClose = () => {
      setIsOpen(false);
      setSelectedCourse("");
      setSelectedInstructorIds([]);
      setSelectedTaIds([]);
      setSelectedAssignment("");
      clearProjectLinkParams();
    };

    const handleCourseChange = (courseId: string) => {
      setSelectedCourse(courseId);
      setSelectedInstructorIds([]);
      setSelectedTaIds([]);
      setSelectedAssignment("");
    };

    const toggleInstructor = (instructorId: string) => {
      setSelectedInstructorIds((prev) =>
        prev.includes(instructorId)
          ? prev.filter((id) => id !== instructorId)
          : [...prev, instructorId],
      );
    };

    const toggleTA = (taId: string) => {
      setSelectedTaIds((prev) =>
        prev.includes(taId)
          ? prev.filter((id) => id !== taId)
          : [...prev, taId],
      );
    };

    const handleLinkProject = async () => {
      if (
        !selectedCourse ||
        !selectedAssignment ||
        (selectedInstructorIds.length === 0 && selectedTaIds.length === 0)
      ) {
        toast.error(
          "Please select a course, assignment, and at least one instructor or TA",
        );
        return;
      }

      try {
        await linkProjectMutation.mutateAsync({
          resourceLinkId: selectedAssignment,
          projectId,
          instructor_ids: selectedInstructorIds,
          teaching_ass_id: selectedTaIds,
        });
        refetchProjects();
        clearProjectLinkParams();
        toast.success("Project linked successfully!");
        handleClose();
      } catch (error) {
        toast.error("Failed to link project. Please try again.");
      }
    };

    const isLinkDisabled =
      !selectedCourse ||
      !selectedAssignment ||
      (selectedInstructorIds.length === 0 && selectedTaIds.length === 0) ||
      linkProjectMutation.isPending;

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-sm bg-white border transparent-overlay shadow-lg max-h-[95vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg font-semibold">
              Link Project to LMS Assignment
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-2 overflow-x-visible">
            {/* Step 1: Course Selection */}
            <div>
              <Label htmlFor="course" className="text-sm font-medium">
                Course <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedCourse}
                onValueChange={handleCourseChange}
                disabled={contextsLoading}
              >
                <SelectTrigger id="course" className="mt-2 w-full">
                  <SelectValue
                    placeholder={
                      contextsLoading ? "Loading courses..." : "Select Course"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="z-[150]">
                  {contextsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : contexts.length === 0 ? (
                    <SelectItem value="no-courses" disabled>
                      No courses available
                    </SelectItem>
                  ) : (
                    contexts.map((context) => (
                      <SelectItem key={context.id} value={context.id}>
                        {context.contextTitle} ({context.contextLabel})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Instructors Selection */}
            {selectedCourse && (
              <div>
                <Label className="text-sm font-medium">
                  Instructors <span className="text-red-500">*</span>
                </Label>
                <Card className="mt-2">
                  <CardContent className="p-3">
                    {instructorsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        <span className="ml-2 text-sm text-muted-foreground">
                          Loading...
                        </span>
                      </div>
                    ) : instructorList.length === 0 ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No instructors found
                      </div>
                    ) : (
                      <>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {instructorList.map((instructor) => (
                            <div
                              key={instructor.id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                              onClick={() => toggleInstructor(instructor.id)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedInstructorIds.includes(
                                  instructor.id,
                                )}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {instructor.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {instructor.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {selectedInstructorIds.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-blue-600 font-medium">
                              {selectedInstructorIds.length} instructor
                              {selectedInstructorIds.length > 1 ? "s" : ""}{" "}
                              selected
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Teaching Assistants Selection */}
            {/* {selectedCourse && (
              <div>
                <Label className="text-sm font-medium">
                  Teaching Assistants
                </Label>
                <Card className="mt-2">
                  <CardContent className="p-3">
                    {instructorsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        <span className="ml-2 text-sm text-muted-foreground">
                          Loading...
                        </span>
                      </div>
                    ) : taList.length === 0 ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No teaching assistants found
                      </div>
                    ) : (
                      <>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {taList.map((ta) => (
                            <div
                              key={ta.id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                              onClick={() => toggleTA(ta.id)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTaIds.includes(ta.id)}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {ta.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {ta.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {selectedTaIds.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-blue-600 font-medium">
                              {selectedTaIds.length} TA
                              {selectedTaIds.length > 1 ? "s" : ""} selected
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )} */}

            {/* Step 4: Assignment Selection */}
            {selectedCourse && (
              <div className="relative z-10">
                <Label htmlFor="assignment" className="text-sm font-medium">
                  Assignment <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={selectedAssignment}
                  onValueChange={setSelectedAssignment}
                  disabled={assignmentsLoading}
                >
                  <SelectTrigger
                    id="assignment"
                    className="mt-2 w-full bg-white"
                  >
                    <SelectValue
                      placeholder={
                        assignmentsLoading
                          ? "Loading assignments..."
                          : "Select Assignment"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] bg-white">
                    {assignmentsLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading...
                      </SelectItem>
                    ) : assignments.length === 0 ? (
                      <SelectItem value="no-assignments" disabled>
                        No assignments available
                      </SelectItem>
                    ) : (
                      assignments.map((assignment) => (
                        <SelectItem key={assignment.id} value={assignment.id}>
                          {assignment.resourceLinkTitle}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex gap-2 mt-6 flex-shrink-0">
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1"
              disabled={linkProjectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkProject}
              className="flex-1 bg-bits-blue hover:bg-bits-blue-700"
              disabled={isLinkDisabled}
            >
              {linkProjectMutation.isPending ? "Linking..." : "Link Project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const DeleteProjectModal = ({
    isOpen,
    onClose,
    refetchProjects,
    projectName = "Untitled Project",
    projectId,
  }: {
    isOpen: boolean;
    onClose: () => void;
    refetchProjects: (
      options?: RefetchOptions | undefined,
    ) => Promise<QueryObserverResult<EnhancedProjectsResponseData, Error>>;
    projectName?: string;
    projectId: string;
  }) => {
    const { mutate: deleteProjectMutation, isPending } = useDeleteProject();
    const handleDelete = () => {
      deleteProjectMutation(projectId, {
        onSuccess: () => {
          toast.success("Project deleted successfully");
          refetchProjects();
          onClose(); // close modal after success
        },
        onError: () => {
          onClose();
          toast.error("Failed to delete project");
        },
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md bg-white border transparent-overlay">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black text-[18px]">
              Delete Project?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-gray-700 mb-4 text-[14px]">
              Are you sure you want to delete{" "}
              <span className="font-semibold break-all">{projectName}</span>?
            </p>
          </div>

          <div className="flex flex-row-reverse gap-4 mt-6">
            <button
              onClick={handleDelete}
              disabled={isPending}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
            >
              {isPending ? "Deleting..." : "Delete"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  interface CloseProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetchProjects: () => void;
    projectName?: string;
    projectId: string;
  }

  const DeletePracticeProjectModal = ({
    isOpen,
    onClose,
    refetchProjects,
    projectName = "Untitled Practice Project",
    projectId,
  }: {
    isOpen: boolean;
    onClose: () => void;
    refetchProjects: () => void;
    projectName?: string;
    projectId: string;
  }) => {
    const { mutate: deletePracticeProjectMutation, isPending } =
      useDeletePracticeProjectMutation();

    const handleDelete = () => {
      deletePracticeProjectMutation(projectId, {
        onSuccess: () => {
          toast.success("Practice project deleted successfully");
          refetchProjects();
          onClose();
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message ||
              "Failed to delete practice project",
          );
          onClose();
        },
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md bg-white border transparent-overlay">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black text-[18px]">
              Delete Project?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-gray-700 mb-4 text-[14px]">
              Are you sure you want to delete{" "}
              <span className="font-semibold break-all">{projectName}</span>?
            </p>
          </div>

          <div className="flex flex-row-reverse gap-4 mt-6">
            <button
              onClick={handleDelete}
              disabled={isPending}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
            >
              {isPending ? "Deleting..." : "Delete"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const UnpublishPracticeProjectModal = ({
    isOpen,
    onClose,
    onConfirm,
    projectName = "Untitled Practice Project",
  }: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    projectName?: string;
  }) => {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md bg-white border transparent-overlay">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black text-[18px]">
              Unpublish Project?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-gray-700 mb-4 text-[14px]">
              Are you sure you want to unpublish{" "}
              <span className="font-semibold break-all">{projectName}</span>?
              This will hide the project from students, but existing enrollments
              will remain intact.
            </p>
          </div>

          <div className="flex flex-row-reverse gap-4 mt-6">
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Unpublish
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const CloseProjectModal = ({
    isOpen,
    onClose,
    refetchProjects,
    projectName = "Untitled Project",
    projectId,
  }: CloseProjectModalProps) => {
    const { mutate: closeProjectMutation, isPending: isClosePending } =
      useCloseProject();

    const handleClose = () => {
      closeProjectMutation(projectId, {
        onSuccess: (response: any) => {
          // ✅ Access server response safely
          const message = response?.data?.data?.error?.message;

          if (message) {
            toast.info(message);
          } else {
            toast.success("Project closed successfully");
          }

          refetchProjects();
          onClose();
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            "Failed to close project";

          toast.error(message);
          onClose();
        },
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md bg-white border transparent-overlay">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black text-[18px]">
              Close Project?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-gray-700 mb-4 text-[14px]">
              Are you sure you want to close{" "}
              <span className="font-semibold">{projectName}</span>?
            </p>
          </div>

          <div className="flex flex-row-reverse gap-4 mt-6">
            <button
              onClick={handleClose}
              disabled={isClosePending}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
            >
              {isClosePending ? "Closing..." : "Close"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  const { mutateAsync: duplicateTemplate, isPending: isDuplicating } =
    useDuplicateProjectTemplate();
  const handleUseTemplate = async (templateId: string) => {
    try {
      toast.info("Duplicating template...");

      const response = await duplicateTemplate(templateId);

      if (response?.isSuccess) {
        toast.success(response.message || "Template duplicated successfully!");

        refetchProjects();
        setActiveTab("all-projects");
      } else {
        toast.error(
          response?.data?.data?.message || "Failed to duplicate template",
        );
      }
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <>
        {/* Header */}
        <PageHeader
          title="Project Management"
          subtitle="Manage all projects and templates across the platform"
        >
          <Button
            className="bg-bits-blue hover:bg-bits-blue-700"
            onClick={() => {
              setIsModalOpen2(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="text-sm font-semibold">Create Project</span>
          </Button>
        </PageHeader>

        <CreateNewProjectModal
          isOpen={isModalOpen2}
          selectedOption={creationType}
          onClose={handleModalClose}
          onOptionChange={(value) =>
            setCreationType(value as "newProject" | "useTemplate")
          }
          onContinue={handleContinue}
        />
        <SelectProjectTemplate
          isOpen={showTemplateSelector}
          refetch={refetchProjects}
          onClose={() => setShowTemplateSelector(false)}
        />

        <LinkProjectModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          refetchProjects={refetchProjects}
          projectId={selectedProjectIdToLink}
          createdBy={selectedCreatedBy}
        />

        {/* Stats Cards Carousel */}
        <div className="relative mb-6 group">
          <div className="overflow-hidden relative">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${statsCarouselIndex * 100}%)`,
              }}
            >
              {/* Slide 1: Regular Projects Stats */}
              <div className="min-w-full flex-shrink-0 px-1">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard
                    title="Total Projects"
                    value={stats?.totalProjectsCount ?? 0}
                  />
                  <StatCard
                    title="Active Projects"
                    value={stats?.totalActiveProjectsCount ?? 0}
                  />
                  <StatCard
                    title="Completed"
                    value={stats?.totalCompletedProjectsCount ?? 0}
                  />
                  <StatCard
                    title="Templates"
                    value={stats?.totalTemplateProjectsCount ?? 0}
                  />
                </div>
              </div>

              {/* Slide 2: Practice Projects Stats */}
              <div className="min-w-full flex-shrink-0 px-1">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard
                    title="Total Practice Projects"
                    value={practiceProjectsStats?.data?.totalProjects || 0}
                  />
                  <StatCard
                    title="Practice Project Published"
                    value={practiceProjectsStats?.data?.publishedProjects || 0}
                  />
                  <StatCard
                    title="Practice Project Drafts"
                    value={practiceProjectsStats?.data?.draftProjects || 0}
                  />
                  <StatCard
                    title="Total Enrollments"
                    value={practiceProjectsStats?.data?.totalEnrollments || 0}
                  />
                </div>
              </div>
            </div>

            {/* Navigation Arrows - Left */}
            {statsCarouselIndex > 0 && (
              <button
                onClick={() => setStatsCarouselIndex(0)}
                className="absolute left-0 inset-y-0 bg-white/50 px-2 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 flex items-center justify-center rounded-l-lg"
                aria-label="Previous stats"
              >
                <ChevronLeft className="h-8 w-8 text-gray-700" />
              </button>
            )}

            {/* Navigation Arrows - Right */}
            {statsCarouselIndex === 0 && (
              <button
                onClick={() => setStatsCarouselIndex(1)}
                className="absolute right-0 inset-y-0 bg-white/50 px-2 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10 flex items-center justify-center rounded-r-lg"
                aria-label="Next stats"
              >
                <ChevronRight className="h-8 w-8 text-gray-700" />
              </button>
            )}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            <button
              onClick={() => setStatsCarouselIndex(0)}
              className={`h-2 w-2 rounded-full transition-colors ${
                statsCarouselIndex === 0 ? "bg-bits-blue" : "bg-gray-300"
              }`}
              aria-label="Go to slide 1"
            />
            <button
              onClick={() => setStatsCarouselIndex(1)}
              className={`h-2 w-2 rounded-full transition-colors ${
                statsCarouselIndex === 1 ? "bg-bits-blue" : "bg-gray-300"
              }`}
              aria-label="Go to slide 2"
            />
          </div>
        </div>

        {/* Tabs */}
        <TabNavigation
          tabs={[
            {
              key: "all-projects",
              label: "All Projects",
              icon: <FileStack className="h-4 w-4" />,
            },
            {
              key: "templates",
              label: "Templates",
              icon: <FileSymlink className="h-4 w-4" />,
            },
            {
              key: "practice-projects",
              label: "Practice Projects",
              icon: <Bookmark className="h-4 w-4" />,
            },
          ]}
          activeTab={activeTab}
          onTabChange={(tab) =>
            setActiveTab(
              tab as
                | "all-projects"
                | "templates"
                | "practice-projects"
                | "analytics",
            )
          }
          className="mb-6"
        />

        {/* Tab Content */}
        {activeTab === "all-projects" &&
          renderAllProjectsTab({
            searchQuery,
            setSearchQuery,
            projects: enhancedProjectsData?.projects,
            pagination: enhancedProjectsData?.pagination,
            projectsLoading,
            projectsError,
            refetchProjects,
            page,
            setPage,
            limit,
            setLimit,
            courseFilter,
            setCourseFilter,
            statusFilter,
            setStatusFilter,
            difficultyFilter,
            setDifficultyFilter,
            isLinkedFilter,
            setIsLinkedFilter,
            typeFilter,
            setTypeFilter,
            staffLoading,
            courseStaff,
            staffData,
            createdBy,
            setCreatedBy,
            inputValue,
            handleSearchChange,
            typedCourses,
            coursesLoading,
            user,
            navigate,
          })}
        {activeTab === "practice-projects" && renderPracticeProjectsTab()}
        {activeTab === "templates" && <TemplatesTab />}
        {activeTab === "analytics" && <AnalyticsTab />}
        {isOpenSelectTemplate && (
          <ShowSelectTemplateModal
            isOpenSelectTemplate={isOpenSelectTemplate}
            setIsSelectTemplate={setIsSelectTemplate}
            setTrackSelectTemplate={setTrackSelectTemplate}
          />
        )}

        {/* Convert to Template Dialog - Rendered at page level to prevent unmounting */}
        <ConvertToTemplateDialog
          isOpen={!!templateDialogProjectId}
          onOpenChange={(open) => {
            if (!open) setTemplateDialogProjectId(undefined);
          }}
          projectId={templateDialogProjectId}
          refetchProjects={refetchProjects}
        />
      </>
    </div>
  );
}
